import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { MouseEvent, useCallback, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { AddCircle, Clear, ContentCopy, DataObject, Delete, Edit, Event, OpenInNew, PictureAsPdf, Refresh, Search, UploadFile } from '@mui/icons-material';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	InputAdornment,
	Link as MuiLink,
	Paper,
	Popover,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridPaginationModel, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';

import { FileClient, FileExpirationRequest, FileParameter, FileViewModel } from '@@api';
import {
	AttributeKeyAutocomplete,
	formatTimestampFull,
	useApiClient,
	useCopyToClipboard,
	useDataGridSearchParams,
	useIsMobile,
	useToastService,
} from '@@common';
import { AttributeCountBadge, BulkSelectionActions, ConfirmDeleteDialog, GalleryHeader, GalleryStatistics, getServerGridRowCount } from '@@components';

const pageSizeOptions = [10, 20, 50];
const initialPaginationModel: GridPaginationModel = { pageSize: pageSizeOptions[1], page: 0 };
const initialSortModel: GridSortModel = [{ field: 'created', sort: 'desc' }];
const sortingOrder = ['desc', 'asc'] as const;
const initialFilters = {
	fileId: '',
	attributeKey: '',
	attributeValue: '',
};
const expirationPresetDays = [1, 7, 30, 90];

const sortFieldName: Record<string, string> = {
	created: 'Created',
};

type AttributeInput = {
	id: number;
	key: string;
	value: string;
};

function formatFileSize(size: number | undefined): string {
	if (size === undefined || size === null) {
		return '';
	}

	if (size < 1024) {
		return `${size} B`;
	}

	const units = ['KB', 'MB', 'GB', 'TB'];
	let value = size / 1024;
	let unitIndex = 0;

	while (value >= 1024 && unitIndex < units.length - 1) {
		value /= 1024;
		unitIndex += 1;
	}

	return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[unitIndex]}`;
}

function isImageFile(file: FileViewModel): boolean {
	return !!file.url && !!file.contentType?.toLowerCase().startsWith('image/');
}

function isVideoFile(file: FileViewModel): boolean {
	return !!file.url && !!file.contentType?.toLowerCase().startsWith('video/');
}

function isPdfFile(file: FileViewModel): boolean {
	return !!file.url && file.contentType?.toLowerCase() === 'application/pdf';
}

function isJsonFile(file: FileViewModel): boolean {
	return !!file.url && file.contentType?.toLowerCase() === 'application/json';
}

function toDateTimeLocalValue(value: string | undefined): string {
	if (!value) {
		return '';
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}

	const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
	return localDate.toISOString().slice(0, 16);
}

function getFutureDateTimeLocalValue(daysFromNow: number): string {
	const date = new Date();
	date.setDate(date.getDate() + daysFromNow);
	return toDateTimeLocalValue(date.toISOString());
}

function JsonPreview({ file }: { file: FileViewModel }) {
	const { data, isLoading, error } = useQuery({
		queryKey: ['files', 'json-preview', file.id, file.url],
		queryFn: async () => {
			const response = await fetch(file.url!);
			if (!response.ok) {
				throw new Error('Failed to load JSON preview.');
			}

			return response.text();
		},
	});

	return (
		<Box
			component="pre"
			sx={{
				width: 560,
				height: 420,
				maxWidth: '80vw',
				maxHeight: '80vh',
				m: 0,
				p: 1,
				overflow: 'auto',
				fontFamily: 'monospace',
				fontSize: 12,
				whiteSpace: 'pre-wrap',
				bgcolor: 'background.default',
				borderRadius: 1,
			}}
		>
			{isLoading ? 'Loading JSON...' : error ? (error instanceof Error ? error.message : 'Failed to load JSON preview.') : data}
		</Box>
	);
}

function MediaPreviewCell({ file }: { file: FileViewModel }) {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const closeTimerRef = useRef<number | undefined>();
	const isImage = isImageFile(file);
	const isVideo = isVideoFile(file);
	const isPdf = isPdfFile(file);
	const isJson = isJsonFile(file);

	if (!isImage && !isVideo && !isPdf && !isJson) {
		return null;
	}

	const cancelClose = () => {
		if (closeTimerRef.current) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = undefined;
		}
	};

	const handleMouseEnter = (event: MouseEvent<HTMLElement>) => {
		cancelClose();
		setAnchorEl(event.currentTarget);
	};

	const handleMouseLeave = () => {
		closeTimerRef.current = window.setTimeout(() => setAnchorEl(null), 120);
	};

	return (
		<Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
			{isImage && (
				<Box
					component="img"
					src={file.url}
					alt={file.name}
					loading="lazy"
					sx={{
						width: 48,
						height: 48,
						objectFit: 'cover',
						borderRadius: 1,
						border: 1,
						borderColor: 'divider',
						bgcolor: 'background.default',
					}}
				/>
			)}
			{isVideo && (
				<Box
					component="video"
					src={file.url}
					muted
					preload="metadata"
					sx={{
						width: 48,
						height: 48,
						objectFit: 'cover',
						borderRadius: 1,
						border: 1,
						borderColor: 'divider',
						bgcolor: 'common.black',
					}}
				/>
			)}
			{isPdf && (
				<Box
					sx={{
						width: 48,
						height: 48,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						borderRadius: 1,
						border: 1,
						borderColor: 'divider',
						bgcolor: 'background.default',
					}}
				>
					<PictureAsPdf color="error" />
				</Box>
			)}
			{isJson && (
				<Box
					sx={{
						width: 48,
						height: 48,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						borderRadius: 1,
						border: 1,
						borderColor: 'divider',
						bgcolor: 'background.default',
					}}
				>
					<DataObject color="primary" />
				</Box>
			)}
			<Popover
				open={!!anchorEl}
				anchorEl={anchorEl}
				onClose={handleMouseLeave}
				disableRestoreFocus
				sx={{ pointerEvents: 'none' }}
				anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
				transformOrigin={{ vertical: 'center', horizontal: 'left' }}
				PaperProps={{ onMouseEnter: cancelClose, onMouseLeave: handleMouseLeave, sx: { p: 1, ml: 1, pointerEvents: 'auto' } }}
			>
				{isImage && (
					<Box
						component="img"
						src={file.url}
						alt={file.name}
						sx={{
							display: 'block',
							maxWidth: 360,
							maxHeight: 280,
							objectFit: 'contain',
							borderRadius: 1,
						}}
					/>
				)}
				{isVideo && (
					<Box
						component="video"
						src={file.url}
						controls
						preload="metadata"
						sx={{
							display: 'block',
							width: 420,
							maxWidth: '70vw',
							maxHeight: 320,
							bgcolor: 'common.black',
							borderRadius: 1,
						}}
					/>
				)}
				{isPdf && (
					<Box
						component="iframe"
						src={file.url}
						title={file.name}
						sx={{
							display: 'block',
							width: 560,
							height: 640,
							maxWidth: '80vw',
							maxHeight: '80vh',
							border: 0,
							borderRadius: 1,
						}}
					/>
				)}
				{isJson && <JsonPreview file={file} />}
			</Popover>
		</Box>
	);
}

export default function FileListView() {
	const client = useApiClient(FileClient);
	const queryClient = useQueryClient();
	const copyToClipboard = useCopyToClipboard();
	const toastService = useToastService();
	const location = useLocation();
	const isMobile = useIsMobile();

	const { paginationModel, setPaginationModel, sortModel, setSortModel, filters, setFilter, setFilters } = useDataGridSearchParams({
		initialPaginationModel,
		initialSortModel,
		initialFilters,
	});
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [uploadFile, setUploadFile] = useState<File | null>(null);
	const [uploadAttributes, setUploadAttributes] = useState<AttributeInput[]>([]);
	const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [expirationDialogFile, setExpirationDialogFile] = useState<FileViewModel | null>(null);
	const [expirationDialogMode, setExpirationDialogMode] = useState<'single' | 'bulk'>('single');
	const [expirationValue, setExpirationValue] = useState('');

	const { fileId, attributeKey, attributeValue } = filters;
	const normalizedFileId = fileId.trim();
	const normalizedAttributeKey = attributeKey.trim();
	const normalizedAttributeValue = attributeValue.trim();
	const fileDetailSearch = location.search;

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['files', 'list', paginationModel, sortModel, normalizedFileId, normalizedAttributeKey, normalizedAttributeValue],
		queryFn: () => {
			const sortBy = sortModel.length > 0 ? sortFieldName[sortModel[0].field] : undefined;
			const sortDirection = sortModel.length > 0 ? sortModel[0].sort === 'asc' : undefined;
			return client.search(
				normalizedFileId || undefined,
				normalizedAttributeKey || undefined,
				normalizedAttributeValue || undefined,
				paginationModel.page,
				paginationModel.pageSize,
				sortBy,
				sortDirection,
				undefined,
			);
		},
	});

	const rowCount = useMemo(() => getServerGridRowCount(data, paginationModel), [paginationModel, data]);
	const selectedFileIds = useMemo(() => rowSelectionModel.map((id) => String(id)), [rowSelectionModel]);
	const selectedFileCount = selectedFileIds.length;
	const expirationTargetFileIds = expirationDialogMode === 'bulk' ? selectedFileIds : expirationDialogFile ? [expirationDialogFile.id] : [];
	const expirationTargetCount = expirationTargetFileIds.length;
	const expirationDialogOpen = expirationDialogMode === 'bulk' || !!expirationDialogFile;

	const uploadMutation = useMutation({
		mutationFn: async () => {
			if (!uploadFile) {
				throw new Error('Choose a file to upload.');
			}

			const attributes = uploadAttributes.reduce<Record<string, string>>((result, attribute) => {
				const key = attribute.key.trim();
				if (key) {
					result[key] = attribute.value;
				}
				return result;
			}, {});

			const fileParameter: FileParameter = {
				data: uploadFile,
				fileName: uploadFile.name,
			};

			return client.upload(undefined, fileParameter, Object.keys(attributes).length > 0 ? JSON.stringify(attributes) : undefined);
		},
		onSuccess: async () => {
			toastService.success('File uploaded.');
			setUploadDialogOpen(false);
			setUploadFile(null);
			setUploadAttributes([]);
			await queryClient.invalidateQueries({ queryKey: ['files', 'list'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to upload file.'),
	});

	const deleteMutation = useMutation({
		mutationFn: async () => {
			await Promise.all(selectedFileIds.map((id) => client.delete(id)));
		},
		onSuccess: async () => {
			const deletedCount = selectedFileIds.length;
			toastService.success(`${deletedCount} ${deletedCount === 1 ? 'file' : 'files'} deleted.`);
			setDeleteDialogOpen(false);
			setRowSelectionModel([]);
			await queryClient.invalidateQueries({ queryKey: ['files', 'list'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to delete selected files.'),
	});

	const expirationMutation = useMutation({
		mutationFn: async (request: { fileIds: string[]; body: FileExpirationRequest }) => {
			await Promise.all(request.fileIds.map((fileId) => client.setExpiry(fileId, request.body)));
		},
		onSuccess: async (_, request) => {
			const updatedCount = request.fileIds.length;
			const fileLabel = updatedCount === 1 ? 'file' : 'files';
			toastService.success(
				request.body.clearExpiration ? `${updatedCount} ${fileLabel} expiration cleared.` : `${updatedCount} ${fileLabel} expiration updated.`,
			);
			setExpirationDialogFile(null);
			setExpirationDialogMode('single');
			setExpirationValue('');
			await queryClient.invalidateQueries({ queryKey: ['files', 'list'] });
		},
		onError: (error) => toastService.error(error instanceof Error ? error.message : 'Failed to update file expiration.'),
	});

	const clearFilters = () => {
		setFilters(initialFilters, true);
	};

	const addUploadAttribute = () => {
		setUploadAttributes((current) => [...current, { id: Date.now(), key: '', value: '' }]);
	};

	const updateUploadAttribute = (id: number, patch: Partial<AttributeInput>) => {
		setUploadAttributes((current) => current.map((attribute) => (attribute.id === id ? { ...attribute, ...patch } : attribute)));
	};

	const removeUploadAttribute = (id: number) => {
		setUploadAttributes((current) => current.filter((attribute) => attribute.id !== id));
	};

	const openExpirationDialog = useCallback((file: FileViewModel) => {
		setExpirationDialogMode('single');
		setExpirationDialogFile(file);
		setExpirationValue(toDateTimeLocalValue(file.expiresAt));
	}, []);

	const openBulkExpirationDialog = () => {
		setExpirationDialogMode('bulk');
		setExpirationDialogFile(null);
		setExpirationValue('');
	};

	const closeExpirationDialog = () => {
		setExpirationDialogFile(null);
		setExpirationDialogMode('single');
		setExpirationValue('');
	};

	const saveExpiration = () => {
		if (expirationTargetCount === 0 || !expirationValue) {
			return;
		}

		expirationMutation.mutate({
			fileIds: expirationTargetFileIds,
			body: {
				expiresAt: new Date(expirationValue).toISOString(),
				clearExpiration: false,
			},
		});
	};

	const clearExpiration = () => {
		if (expirationTargetCount === 0) {
			return;
		}

		expirationMutation.mutate({
			fileIds: expirationTargetFileIds,
			body: {
				clearExpiration: true,
			},
		});
	};

	const columns = useMemo<GridColDef<FileViewModel>[]>(
		() => [
			{
				field: 'thumbnail',
				headerName: '',
				sortable: false,
				filterable: false,
				width: 72,
				disableColumnMenu: true,
				renderCell: (params) => <MediaPreviewCell file={params.row} />,
			},
			{
				field: 'name',
				headerName: 'File Name',
				sortable: false,
				flex: 1.6,
				renderCell: (params) => (
					<MuiLink
						component={RouterLink}
						to={{ pathname: `/file/${params.row.id}`, search: fileDetailSearch }}
						sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
					>
						{params.row.name}
					</MuiLink>
				),
			},
			{
				field: 'size',
				headerName: 'Size',
				sortable: false,
				width: 120,
				valueGetter: (value: number | undefined) => formatFileSize(value),
			},
			{
				field: 'contentType',
				headerName: 'Content Type',
				sortable: false,
				flex: 1,
			},
			{
				field: 'attributes',
				headerName: 'Attributes',
				sortable: false,
				width: 120,
				renderCell: (params) => <AttributeCountBadge attributes={params.row.attributes} />,
			},
			{
				field: 'created',
				headerName: 'Created',
				sortable: true,
				flex: 0.8,
				valueGetter: (value: string | undefined) => (value ? formatTimestampFull(value) : ''),
			},
			{
				field: 'expiresAt',
				headerName: 'Expires On',
				sortable: false,
				flex: 0.9,
				minWidth: 190,
				renderCell: (params) => (
					<Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%', minWidth: 0 }}>
						<Typography variant="body2" noWrap title={params.row.expiresAt ? formatTimestampFull(params.row.expiresAt) : 'No expiration'} sx={{ minWidth: 0 }}>
							{params.row.expiresAt ? formatTimestampFull(params.row.expiresAt) : 'No expiration'}
						</Typography>
						<IconButton size="small" aria-label="Edit expiration" onClick={() => openExpirationDialog(params.row)}>
							<Edit fontSize="small" />
						</IconButton>
					</Stack>
				),
			},
			{
				field: 'url',
				headerName: 'Open',
				sortable: false,
				width: 100,
				renderCell: (params) =>
					params.row.url ? (
						<MuiLink href={params.row.url} target="_blank" rel="noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
							Open
							<OpenInNew sx={{ fontSize: 16 }} />
						</MuiLink>
					) : (
						''
					),
			},
			{
				field: 'actions',
				type: 'actions',
				headerName: 'Actions',
				width: 80,
				getActions: (params) => [
					<GridActionsCellItem icon={<ContentCopy />} label="Copy ID" onClick={() => copyToClipboard(params.row.id, String)} showInMenu />,
				],
			},
		],
		[copyToClipboard, fileDetailSearch, isMobile, openExpirationDialog],
	);

	const files = data?.items ?? [];
	const filesWithAttributes = files.filter((file) => (file.attributes?.length ?? 0) > 0).length;

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, p: (theme) => `${theme.layout.edgeGap}px`, overflow: 'hidden' }}>
			<GalleryHeader
				title="Files"
				subtitle="Store, organize, and share files across your workspace."
				utilityActions={<Tooltip title="Refresh files"><IconButton aria-label="Refresh files" onClick={() => refetch()}><Refresh /></IconButton></Tooltip>}
				primaryAction={<Button variant="contained" startIcon={<UploadFile />} onClick={() => setUploadDialogOpen(true)}>Upload file</Button>}
				statistics={<GalleryStatistics items={[{ label: 'Files on this page', value: files.length }, { label: 'With attributes', value: filesWithAttributes }, { label: 'Selected', value: selectedFileCount }]} />}
				search={
					<TextField
						size="small"
						placeholder="Search file ID…"
						inputProps={{ 'aria-label': 'Search file ID' }}
						value={fileId}
						onChange={(event) => setFilter('fileId', event.target.value, true)}
						fullWidth
						InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>, endAdornment: fileId ? <InputAdornment position="end"><IconButton aria-label="Clear file ID search" size="small" onClick={() => setFilter('fileId', '', true)}><Clear fontSize="small" /></IconButton></InputAdornment> : undefined }}
					/>
				}
			/>

			<Paper
				variant="outlined"
				sx={{
					px: { xs: 1, sm: 2 },
					py: 1.5,
				}}
			>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1, alignItems: { xs: 'stretch', sm: 'center' } }}>
				<AttributeKeyAutocomplete
					label="Attribute"
					value={attributeKey}
					onChange={(value) => setFilter('attributeKey', value, true)}
					sx={{ width: { xs: '100%', sm: 220 } }}
				/>
				<TextField
					size="small"
					label="Value"
					value={attributeValue}
					onChange={(event) => {
						setFilter('attributeValue', event.target.value, true);
					}}
					sx={{ width: { xs: '100%', sm: 220 } }}
					InputProps={{
						endAdornment: attributeValue ? (
							<InputAdornment position="end">
								<IconButton aria-label="Clear value" size="small" onClick={() => setFilter('attributeValue', '', true)}>
									<Clear fontSize="small" />
								</IconButton>
							</InputAdornment>
						) : undefined,
					}}
				/>
				<IconButton aria-label="Clear filters" title="Clear filters" onClick={clearFilters}>
					<Clear />
				</IconButton>
				<Box sx={{ flex: '1 1 auto', minWidth: 24 }} />
				<BulkSelectionActions
					selectedCount={selectedFileCount}
					onClearSelection={() => setRowSelectionModel([])}
					onDeleteSelected={() => setDeleteDialogOpen(true)}
					deleteLabel="Delete selected files"
					deleteDisabled={deleteMutation.isPending}
				>
					<IconButton
						size="small"
						aria-label="Set expiration for selected files"
						title="Set expiration for selected files"
						onClick={openBulkExpirationDialog}
						disabled={expirationMutation.isPending || selectedFileCount === 0}
					>
						<Event fontSize="small" />
					</IconButton>
				</BulkSelectionActions>
				</Stack>
			</Paper>

			<Paper sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
				<DataGrid
					loading={isLoading}
					rows={data?.items ?? []}
					rowCount={rowCount}
					columns={columns}
					paginationMode="server"
					sortingMode="server"
					disableColumnFilter
					pageSizeOptions={pageSizeOptions}
					paginationModel={paginationModel}
					onPaginationModelChange={setPaginationModel}
					sortModel={sortModel}
					onSortModelChange={setSortModel}
					sortingOrder={sortingOrder}
					checkboxSelection
					disableRowSelectionOnClick
					rowSelectionModel={rowSelectionModel}
					onRowSelectionModelChange={setRowSelectionModel}
					columnVisibilityModel={{
						thumbnail: !isMobile,
						size: !isMobile,
						contentType: !isMobile,
						attributes: !isMobile,
						created: !isMobile,
						expiresAt: !isMobile,
						url: !isMobile,
					}}
					density={isMobile ? 'compact' : 'standard'}
					sx={{ border: 0, minWidth: 0 }}
					rowHeight={isMobile ? 48 : 64}
				/>
			</Paper>

			<Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Upload File</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Box>
							<Button variant="outlined" component="label" startIcon={<UploadFile />}>
								Choose File
								<input
									hidden
									type="file"
									onChange={(event) => {
										setUploadFile(event.target.files?.[0] ?? null);
										event.target.value = '';
									}}
								/>
							</Button>
							<Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
								{uploadFile ? `${uploadFile.name} (${formatFileSize(uploadFile.size)})` : 'No file selected'}
							</Typography>
						</Box>

						<Stack spacing={1}>
							<Stack direction="row" alignItems="center" justifyContent="space-between">
								<Typography variant="subtitle2">Attributes</Typography>
								<Button size="small" startIcon={<AddCircle />} onClick={addUploadAttribute}>
									Add
								</Button>
							</Stack>
							{uploadAttributes.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No attributes
								</Typography>
							) : (
								uploadAttributes.map((attribute) => (
									<Stack key={attribute.id} direction="row" spacing={1} alignItems="center">
										<AttributeKeyAutocomplete
											label="Attribute"
											value={attribute.key}
											onChange={(value) => updateUploadAttribute(attribute.id, { key: value })}
											sx={{ flex: 1 }}
										/>
										<TextField
											size="small"
											label="Value"
											value={attribute.value}
											onChange={(event) => updateUploadAttribute(attribute.id, { value: event.target.value })}
											sx={{ flex: 1 }}
										/>
										<IconButton aria-label="Remove attribute" onClick={() => removeUploadAttribute(attribute.id)}>
											<Delete fontSize="small" />
										</IconButton>
									</Stack>
								))
							)}
						</Stack>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={() => uploadMutation.mutate()} disabled={!uploadFile || uploadMutation.isPending}>
						Upload
					</Button>
				</DialogActions>
			</Dialog>

			<ConfirmDeleteDialog
				open={deleteDialogOpen}
				title="Delete Files"
				resourceName="file"
				selectedCount={selectedFileCount}
				isDeleting={deleteMutation.isPending}
				onClose={() => setDeleteDialogOpen(false)}
				onConfirm={() => deleteMutation.mutate()}
			/>

			<Dialog open={expirationDialogOpen} onClose={closeExpirationDialog} fullWidth maxWidth="xs">
				<DialogTitle>{expirationDialogMode === 'bulk' ? 'Bulk File Expiration' : 'File Expiration'}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<Typography variant="body2" color="text.secondary" noWrap title={expirationDialogMode === 'bulk' ? undefined : expirationDialogFile?.name}>
							{expirationDialogMode === 'bulk' ? `${selectedFileCount} selected ${selectedFileCount === 1 ? 'file' : 'files'}` : expirationDialogFile?.name}
						</Typography>
						<Stack spacing={1}>
							<Typography variant="subtitle2">Expire From Now</Typography>
							<Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
								{expirationPresetDays.map((days) => (
									<Button key={days} size="small" variant="outlined" onClick={() => setExpirationValue(getFutureDateTimeLocalValue(days))}>
										{days} {days === 1 ? 'day' : 'days'}
									</Button>
								))}
							</Stack>
						</Stack>
						<TextField
							size="small"
							label="Expires On"
							type="datetime-local"
							value={expirationValue}
							onChange={(event) => setExpirationValue(event.target.value)}
							InputLabelProps={{ shrink: true }}
							fullWidth
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeExpirationDialog} disabled={expirationMutation.isPending}>
						Cancel
					</Button>
					<Button
						color="inherit"
						onClick={clearExpiration}
						disabled={expirationMutation.isPending || expirationTargetCount === 0 || (expirationDialogMode === 'single' && !expirationDialogFile?.expiresAt)}
					>
						Clear Expiration
					</Button>
					<Button variant="contained" onClick={saveExpiration} disabled={expirationMutation.isPending || expirationTargetCount === 0 || !expirationValue}>
						Save
					</Button>
				</DialogActions>
			</Dialog>
		</Stack>
	);
}
